import { type PayloadAction, createSlice } from "@reduxjs/toolkit";

import { RootState } from "../store";

import { UserProps } from "@/app/app.interface";
import { APP_LOCAL_KEYS, States } from "@/app/constants/constants";
import { CustomerAuthUser, CustomerVerified } from "@/features/customerPortal";
import { Workspace } from "@/features/workspace/workspace.interfaces";
import { Info } from "@/interfaces/general.interface";
import { typedLocalStorage } from "@/utils/storage/localStorage";

const getInitialUserData = (): UserProps | null => {
  return typedLocalStorage.getItem(
    APP_LOCAL_KEYS.USER_DATA,
  ) as UserProps | null;
};
const getInitialUserToken = (): string | null => {
  return typedLocalStorage.getItem(APP_LOCAL_KEYS.TOKEN);
};
const getWorkspaceData = (): Workspace | null => {
  return typedLocalStorage.getItem(
    APP_LOCAL_KEYS.WORKSPACE_DATA,
  ) as Workspace | null;
};
const getCustomerData = (): CustomerVerified | null => {
  return typedLocalStorage.getItem(
    APP_LOCAL_KEYS.CUSTOMER_DATA,
  ) as CustomerVerified | null;
};

const isSyncWithXero = (workspace: Workspace | null): boolean => {
  if (!workspace) {
    return false;
  }

  return workspace?.isSyncedWithXero || false;
};

const getCustomerAuthUser = (): CustomerAuthUser | null => {
  return typedLocalStorage.getItem(
    APP_LOCAL_KEYS.CUSTOMER_AUTH_USER,
  ) as CustomerAuthUser | null;
};

const getWorkspaceTimeZone = (workspace: Workspace | null): string => {
  if (!workspace) {
    return "";
  }

  return (
    workspace?.timezone ||
    States.find((state) => workspace?.state === state.name)?.timezone ||
    ""
  );
};

const isAdminLoginAsCustomer = (): boolean => {
  return typedLocalStorage.getItemOrDefault(
    APP_LOCAL_KEYS.IS_ADMIN_LOGIN_AS_CUSTOMER,
    false,
  );
};

const getAdminAsCustomer = (): Info | null => {
  return typedLocalStorage.getItem(
    APP_LOCAL_KEYS.ADMIN_AS_CUSTOMER,
  ) as Info | null;
};

const getCustomerUserId = (): string | null => {
  return typedLocalStorage.getItem(APP_LOCAL_KEYS.CUSTOMER_USER_ID);
};

const initialState = {
  userData: getInitialUserData(),
  userToken: getInitialUserToken(),
  workspaceData: getWorkspaceData(),
  customerData: getCustomerData(),
  isWorkspaceConnectedWithXero: false,
  customerAuthUser: getCustomerAuthUser(),
  isAdminLoginAsCustomer: isAdminLoginAsCustomer(),
  adminAsCustomer: getAdminAsCustomer(),
  customerUserId: getCustomerUserId(),
};
export const userDataSlice = createSlice({
  name: "userData",
  initialState,
  reducers: {
    refetchUserData: (state) => {
      state.userData = getInitialUserData();
      state.userToken = getInitialUserToken();
    },
    refetchWorkspaceData: (state) => {
      state.workspaceData = getWorkspaceData();
    },
    refetchCustomerData: (state) => {
      state.customerData = getCustomerData();
    },
    setWorkspaceSyncedWithXero: (state, action) => {
      state.isWorkspaceConnectedWithXero = action.payload;
    },
    refetchWorkspaceSyncedWithXero: (state) => {
      state.isWorkspaceConnectedWithXero = isSyncWithXero(state.workspaceData);
    },
    setUserDetails: (state, action) => {
      state.userData = action.payload;
    },
    resetCustomerData: (state) => {
      state.customerData = getCustomerData();
    },
    refreshUserToken: (state) => {
      state.userToken = getInitialUserToken();
    },
    refreshCustomerAuthUser: (state) => {
      state.customerAuthUser = getCustomerAuthUser();
    },
    refreshAdminLoginAsCustomer: (state) => {
      state.isAdminLoginAsCustomer = isAdminLoginAsCustomer();
    },
    refreshAdminAsCustomer: (state) => {
      state.adminAsCustomer = getAdminAsCustomer();
    },
    refreshCustomerUserId: (state) => {
      state.customerUserId = getCustomerUserId();
    },
    mergeWorkspaceCachedData: (
      state,
      action: PayloadAction<Partial<Workspace>>,
    ) => {
      if (!state.workspaceData) {
        return;
      }

      state.workspaceData = { ...state.workspaceData, ...action.payload };
    },
  },
});

export const {
  refetchUserData,
  refetchWorkspaceData,
  refetchCustomerData,

  refetchWorkspaceSyncedWithXero,
  setUserDetails,

  refreshUserToken,
  refreshCustomerAuthUser,
  refreshAdminLoginAsCustomer,
  refreshAdminAsCustomer,
  refreshCustomerUserId,
  mergeWorkspaceCachedData,
} = userDataSlice.actions;

export const selectWorkspaceTimeZone = (state: RootState): string =>
  getWorkspaceTimeZone(state.user?.workspaceData);

export const selectWorkspaceXeroLastInvoiceSyncedDate = (
  state: RootState,
): string | null | undefined =>
  state.user?.workspaceData?.xeroLastInvoiceSyncedDate;

export default userDataSlice.reducer;
